import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import {
  Copy, RefreshCw, Trash2, Video, Loader2, KeyRound, Save, Check, MoveRight, Download, Play,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (n) => {
  if (!n) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtEventDT = (val) => {
  // event_datetime comes from <input type="datetime-local"> as "YYYY-MM-DDTHH:MM"
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const statusPill = (s) => {
  if (s === 'complete') return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Complete</Badge>;
  if (s === 'pending')  return <Badge className="bg-yellow-500/20 text-yellow-200 border-yellow-500/30">In progress</Badge>;
  if (s === 'aborted')  return <Badge className="bg-red-500/20 text-red-300 border-red-500/30">Aborted</Badge>;
  return <Badge variant="outline">{s}</Badge>;
};

export default function RawUploadsTab() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passcode, setPasscode] = useState('');
  const [savingPass, setSavingPass] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [events, setEvents] = useState([]);
  // Move dialog state
  const [moveTarget, setMoveTarget] = useState(null); // upload row being moved
  const [moveCategory, setMoveCategory] = useState('heading');
  const [moveDifficulty, setMoveDifficulty] = useState('intermediate');
  const [moveEventId, setMoveEventId] = useState('');
  const [moving, setMoving] = useState(false);
  // Preview player state
  const [previewTarget, setPreviewTarget] = useState(null);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const fetchWithRetry = async (url, attempts = 4) => {
      let lastErr;
      for (let i = 0; i < attempts; i++) {
        try {
          return await axios.get(url, { timeout: 15000 });
        } catch (err) {
          lastErr = err;
          const status = err.response?.status;
          // Retry on network / 5xx / Cloudflare edge issues. Bail immediately on 4xx.
          const transient = !status || status >= 500 || status === 408 || status === 429;
          if (!transient || i === attempts - 1) throw err;
          await new Promise((r) => setTimeout(r, 800 * Math.pow(1.8, i)));
        }
      }
      throw lastErr;
    };
    try {
      const [u, p, e] = await Promise.all([
        fetchWithRetry(`${API_URL}/api/admin/raw-uploads`),
        fetchWithRetry(`${API_URL}/api/admin/raw-passcode`),
        fetchWithRetry(`${API_URL}/api/admin/events`),
      ]);
      setUploads(u.data.uploads || []);
      setPasscode(p.data.passcode || '');
      setEvents(e.data.events || []);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to load uploads';
      toast.error(msg.length > 120 ? 'Temporary hiccup — try Refresh again' : msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-generate thumbnails for complete rows without one. Runs serially
  // (one video at a time) so we don't slam S3 or the admin's memory when the
  // list has many uploads. Tracks in-flight ids in a ref to avoid re-attempts
  // during React re-renders.
  const thumbInFlightRef = useRef(new Set());
  const thumbTriedRef = useRef(new Set());
  useEffect(() => {
    if (!uploads.length) return;
    let cancelled = false;

    const generateOne = async (u) => {
      if (thumbInFlightRef.current.has(u.id) || thumbTriedRef.current.has(u.id)) return;
      thumbInFlightRef.current.add(u.id);
      try {
        // Get the stable share URL (mints token if needed).
        const share = await axios.get(`${API_URL}/api/admin/raw-uploads/${u.id}/share-link`);
        const src = `${window.location.origin}${share.data.share_url}`;
        const thumbUrl = await captureThumbnail(src);
        if (!thumbUrl || cancelled) return;
        await axios.post(`${API_URL}/api/admin/raw-uploads/${u.id}/set-thumbnail`, { thumbnail_url: thumbUrl });
        if (cancelled) return;
        setUploads((prev) => prev.map((x) => x.id === u.id ? { ...x, thumbnail_url: thumbUrl } : x));
      } catch (_) {
        // Silent — best-effort. Retry on next full refresh.
      } finally {
        thumbInFlightRef.current.delete(u.id);
        thumbTriedRef.current.add(u.id);
      }
    };

    // Kick off serially — the queue drains itself as uploads state updates.
    const next = uploads.find(
      (u) => u.status === 'complete' && !u.thumbnail_url && !thumbInFlightRef.current.has(u.id) && !thumbTriedRef.current.has(u.id)
    );
    if (next) generateOne(next);
    return () => { cancelled = true; };
  }, [uploads]);

  const savePasscode = async () => {
    if (passcode.trim().length < 4) { toast.error('Passcode must be at least 4 characters'); return; }
    setSavingPass(true);
    try {
      await axios.put(`${API_URL}/api/admin/raw-passcode`, { passcode: passcode.trim() });
      toast.success('Passcode updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update passcode');
    } finally {
      setSavingPass(false);
    }
  };

  const copyShareLink = async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/raw-uploads/${id}/share-link`);
      // share_url is a path — prepend the current origin to make it absolute.
      const url = `${window.location.origin}${r.data.share_url}`;
      try {
        await navigator.clipboard.writeText(url);
        const exp = r.data.expires_at ? new Date(r.data.expires_at).toLocaleDateString() : '30 days';
        toast.success(`Link copied — works until ${exp}`);
      } catch (_) {
        window.prompt('Copy this share link:', url);
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate link');
    }
  };

  const openPreview = async (upload) => {
    setPreviewTarget(upload);
    setPreviewSrc('');
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/raw-uploads/${upload.id}/share-link`);
      setPreviewSrc(`${window.location.origin}${r.data.share_url}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load preview');
      setPreviewTarget(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewTarget(null);
    setPreviewSrc('');
  };

  const downloadFile = async (id, filename) => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/raw-uploads/${id}/download-url`);
      // Navigate the browser directly — S3 responds with Content-Disposition: attachment
      // so the file will be saved with its original filename.
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start download');
    }
  };

  const rotateLink = async (id, filename) => {    if (!window.confirm(`Rotate the share link for "${filename}"? The old link will stop working immediately.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/raw-uploads/${id}/rotate-link`);
      const url = `${window.location.origin}${r.data.share_url}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('New link copied. Old link disabled.');
      } catch (_) {
        window.prompt('New link (old one is disabled):', url);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rotate link');
    }
  };

  const removeUpload = async (id, filename) => {
    if (!window.confirm(`Delete "${filename}" from S3? This can't be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/raw-uploads/${id}`);
      setUploads((prev) => prev.filter((u) => u.id !== id));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openMoveDialog = (upload) => {
    setMoveTarget(upload);
    setMoveCategory('heading');
    setMoveDifficulty('intermediate');
    setMoveEventId('');
  };

  // Best-effort client-side thumbnail capture. Loads the video into a hidden
  // <video>, seeks to ~10s (or the middle if shorter), draws a frame to a
  // canvas, uploads the JPEG to /api/admin/upload/image, and returns the URL.
  // Any failure resolves to null so the move flow continues without a thumb.
  const captureThumbnail = (videoSrc) => new Promise((resolve) => {
    let done = false;
    const finish = (url) => {
      if (done) return;
      done = true;
      try { video.remove(); } catch (_) { /* noop */ }
      resolve(url);
    };
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.style.position = 'fixed';
    video.style.left = '-9999px';
    video.style.top = '0';
    document.body.appendChild(video);

    const timer = setTimeout(() => finish(null), 20000); // 20s hard cap

    video.addEventListener('loadedmetadata', () => {
      const dur = isFinite(video.duration) ? video.duration : 0;
      const target = dur > 0 ? Math.min(10, Math.max(0, dur - 0.1)) : 0;
      try { video.currentTime = target; } catch (_) { finish(null); }
    });
    video.addEventListener('seeked', async () => {
      try {
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 360;
        const scale = Math.min(1, 640 / Math.max(w, 1));
        const cw = Math.round(w * scale);
        const ch = Math.round(h * scale);
        const canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, cw, ch);
        canvas.toBlob(async (blob) => {
          if (!blob) return finish(null);
          try {
            const fd = new FormData();
            fd.append('file', blob, 'thumb.jpg');
            fd.append('folder', 'thumbnails');
            const r = await axios.post(`${API_URL}/api/admin/upload/image`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 20000,
            });
            clearTimeout(timer);
            finish(r.data?.url || null);
          } catch (_) {
            clearTimeout(timer);
            finish(null);
          }
        }, 'image/jpeg', 0.85);
      } catch (_) {
        clearTimeout(timer);
        finish(null);
      }
    });
    video.addEventListener('error', () => { clearTimeout(timer); finish(null); });

    video.src = videoSrc;
    try { video.load(); } catch (_) { /* noop */ }
  });

  const undoMove = async (uploadId) => {
    try {
      await axios.post(`${API_URL}/api/admin/raw-uploads/${uploadId}/undo-move`);
      setUploads((prev) => prev.map((u) =>
        u.id === uploadId
          ? { ...u, moved_to_library: false, library_video_id: null, event_video_id: null }
          : u
      ));
      toast.success('Move undone — library entry removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Undo failed');
    }
  };

  const submitMove = async () => {
    if (!moveTarget) return;
    setMoving(true);
    try {
      // Step 1: if we already have a thumbnail on the row (auto-generated on
      // list load), reuse it. Otherwise mint a share URL and capture now.
      let thumbnailUrl = moveTarget.thumbnail_url || null;
      if (!thumbnailUrl) {
        let videoSrc = null;
        try {
          const share = await axios.get(`${API_URL}/api/admin/raw-uploads/${moveTarget.id}/share-link`);
          videoSrc = `${window.location.origin}${share.data.share_url}`;
        } catch (_) {
          // Non-fatal — we'll just move without a thumbnail
        }
        if (videoSrc) {
          thumbnailUrl = await captureThumbnail(videoSrc);
        }
      }

      const r = await axios.post(
        `${API_URL}/api/admin/raw-uploads/${moveTarget.id}/move-to-library`,
        {
          category: moveCategory,
          difficulty: moveDifficulty,
          event_id: moveEventId || undefined,
          thumbnail_url: thumbnailUrl || undefined,
        }
      );
      // Update local state so the row shows "In Library" without a refetch.
      setUploads((prev) => prev.map((u) =>
        u.id === moveTarget.id
          ? { ...u, moved_to_library: true, library_video_id: r.data.library_video_id }
          : u
      ));
      const gotoLibrary = () => {
        const trig = document.querySelector('[data-testid="tab-library"]');
        if (trig) trig.click();
      };
      const movedId = moveTarget.id;
      if (r.data.already_moved) {
        toast.info('This upload is already in the Library.', {
          action: { label: 'Open Library tab', onClick: gotoLibrary },
        });
      } else {
        toast.success(
          thumbnailUrl
            ? 'Moved to Library with thumbnail (10s frame)'
            : r.data.thumbnail_url
              ? 'Moved to Library with thumbnail'
              : 'Moved to Library. Thumbnail will be added on the next "Generate Thumbnails" run.',
          {
            duration: 12000,
            action: { label: 'Undo', onClick: () => undoMove(movedId) },
          }
        );
      }
      setMoveTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-raw-uploads-tab">
      {/* Passcode + share-link card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="w-5 h-5 text-primary" /> Camera Drop Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Share this link with your camera operators</div>
            <div className="flex gap-2">
              <Input value={`${window.location.origin}/raw`} readOnly className="font-mono text-sm" />
              <Button
                variant="outline"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/raw`); toast.success('Link copied'); }}
                data-testid="copy-raw-link"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Passcode (share separately)</div>
            <div className="flex gap-2">
              <Input
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="passcode"
                data-testid="raw-passcode-field"
              />
              <Button onClick={savePasscode} disabled={savingPass} data-testid="save-raw-passcode">
                {savingPass ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Anyone with the link + passcode can upload — change it anytime you want to revoke.</p>
          </div>
        </CardContent>
      </Card>

      {/* Uploads list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" /> Uploads
            <span className="text-sm font-normal text-muted-foreground">({uploads.length})</span>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-raw-uploads">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : uploads.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              No uploads yet. Camera operators can drop files at <span className="font-mono text-foreground">{window.location.origin}/raw</span>.
            </div>
          ) : (
            <div className="space-y-2">
              {uploads.map((u) => (
                <div
                  key={u.id}
                  className="border rounded-lg p-3 hover:bg-muted/30 transition"
                  data-testid={`raw-row-${u.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    {u.status === 'complete' && (
                      <button
                        type="button"
                        onClick={() => openPreview(u)}
                        className="shrink-0 w-28 h-16 rounded-md bg-black overflow-hidden relative group border border-border/60 focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid={`thumb-${u.id}`}
                        title="Click to preview"
                      >
                        {u.thumbnail_url ? (
                          <img
                            src={u.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/60">
                            <Video className="w-6 h-6" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play className="w-5 h-5 text-white" fill="white" />
                        </div>
                      </button>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium truncate" title={u.filename}>{u.filename}</span>
                        {statusPill(u.status)}
                      </div>
                      {u.description && (
                        <div className="text-sm text-foreground/90 mb-1">
                          {u.description}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground space-x-3 flex flex-wrap gap-y-1">
                        {u.location && <span>📍 {u.location}</span>}
                        {u.event_datetime && <span>🗓 {fmtEventDT(u.event_datetime)}</span>}
                        <span>{fmtBytes(u.size)}</span>
                        <span className="text-muted-foreground/70">Uploaded {fmtDate(u.created_at)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        by <b className="text-foreground">{u.uploader_name || 'Unknown'}</b>
                      </div>
                      {u.note && !u.description && (
                        <div className="text-xs text-muted-foreground mt-1 italic">&ldquo;{u.note}&rdquo;</div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {u.status === 'complete' && !u.moved_to_library && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openMoveDialog(u)}
                          data-testid={`move-to-library-${u.id}`}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          title="Move this file to the Videos tab / Library"
                        >
                          <MoveRight className="w-4 h-4 mr-1" /> Move to Videos
                        </Button>
                      )}
                      {u.status === 'complete' && u.moved_to_library && (
                        <>
                          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 self-center whitespace-nowrap">
                            <Check className="w-3 h-3 mr-1" /> In Library
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => undoMove(u.id)}
                            data-testid={`undo-move-${u.id}`}
                            title="Remove from Library and put back in the pending list"
                          >
                            Undo
                          </Button>
                        </>
                      )}
                      {u.status === 'complete' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPreview(u)}
                            data-testid={`preview-${u.id}`}
                            title="Preview — play the video without downloading"
                          >
                            <Play className="w-4 h-4 mr-1" /> Play
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => copyShareLink(u.id)}
                            data-testid={`copy-url-${u.id}`}
                            className="bg-primary hover:bg-primary/90"
                          >
                            {copiedId === u.id ? (
                              <><Check className="w-4 h-4 mr-1" /> Copied</>
                            ) : (
                              <><Copy className="w-4 h-4 mr-1" /> Copy Link</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadFile(u.id, u.filename)}
                            data-testid={`download-${u.id}`}
                            title="Download the raw file to this computer"
                          >
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => rotateLink(u.id, u.filename)}
                            data-testid={`rotate-${u.id}`}
                            title="Rotate link — kills the old URL and issues a new one"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeUpload(u.id, u.filename)}
                        className="text-red-500 hover:text-red-600"
                        data-testid={`delete-${u.id}`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Move to Videos dialog */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent className="max-w-md" data-testid="move-to-library-dialog">
          <DialogHeader>
            <DialogTitle>Move to Videos Library</DialogTitle>
            <DialogDescription>
              {moveTarget?.filename}
              {moveTarget?.description ? ` — ${moveTarget.description}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="move-category">Category</Label>
              <Select value={moveCategory} onValueChange={setMoveCategory}>
                <SelectTrigger id="move-category" data-testid="move-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="heading">Heading</SelectItem>
                  <SelectItem value="heeling">Heeling</SelectItem>
                  <SelectItem value="breakaway">Breakaway</SelectItem>
                  <SelectItem value="calf_roping">Calf Roping</SelectItem>
                  <SelectItem value="team_roping">Team Roping</SelectItem>
                  <SelectItem value="horsemanship">Horsemanship</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="move-difficulty">Difficulty</Label>
              <Select value={moveDifficulty} onValueChange={setMoveDifficulty}>
                <SelectTrigger id="move-difficulty" data-testid="move-difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="move-event">Attach to event (optional)</Label>
              <Select value={moveEventId || 'none'} onValueChange={(v) => setMoveEventId(v === 'none' ? '' : v)}>
                <SelectTrigger id="move-event" data-testid="move-event-select">
                  <SelectValue placeholder="Don't attach to an event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Don&apos;t attach to an event</SelectItem>
                  {events.map((ev) => (
                    <SelectItem key={ev.id} value={ev.id}>
                      {ev.title || ev.name || '(untitled)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Also creates an entry on the Event&apos;s gallery if selected. Most recent events are at the top.
              </p>
            </div>
            <div className="text-xs text-muted-foreground border-t pt-3">
              After moving: the file appears in the <b>Videos</b> tab / Library. We&apos;ll try to grab a thumbnail from the 10-second mark automatically — if that fails, run <b>Generate Thumbnails</b> from the Library tab.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveTarget(null)} disabled={moving}>Cancel</Button>
            <Button onClick={submitMove} disabled={moving} data-testid="confirm-move" className="bg-emerald-600 hover:bg-emerald-700">
              {moving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Grabbing frame & moving…</> : <><MoveRight className="w-4 h-4 mr-1" /> Move</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview player dialog */}
      <Dialog open={!!previewTarget} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-3xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate">{previewTarget?.filename}</DialogTitle>
            <DialogDescription className="text-xs">
              {previewTarget?.description || 'Preview — this doesn\u2019t move or share the file.'}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-black rounded-md overflow-hidden aspect-video flex items-center justify-center">
            {previewLoading || !previewSrc ? (
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            ) : (
              <video
                key={previewSrc}
                src={previewSrc}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="w-full h-full"
                data-testid="preview-video"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePreview}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
